import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import { Box, IconButton } from '@mui/material';
import React from 'react';
import { StyledButton } from '../styles/StyledButtons';
import { StyledTypography } from '../styles/styles';

export interface PaginationData {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

interface PaginationProps {
  pagination: PaginationData;
  onPageChange: (page: number) => void;
  loading?: boolean;
  sx?: React.CSSProperties;
  itemLabel?: string;
}

const Pagination: React.FC<PaginationProps> = ({
  pagination,
  onPageChange,
  loading = false,
  sx = {},
  itemLabel = 'articles',
}) => {
  const { page, totalPages, total, limit } = pagination;

  if (totalPages <= 1) {
    return null;
  }

  const getVisiblePages = () => {
    // Calculate how many page numbers we can show
    // We want to show more pages when there are many total pages
    const maxVisiblePages = Math.min(9, totalPages); // Show up to 9 page numbers
    const delta = Math.floor((maxVisiblePages - 2) / 2); // Pages on each side of current page

    const range = [];
    const rangeWithDots = [];

    // Calculate the range around current page
    const start = Math.max(2, page - delta);
    const end = Math.min(totalPages - 1, page + delta);

    for (let i = start; i <= end; i++) {
      range.push(i);
    }

    // Add first page and ellipsis if needed
    if (start > 2) {
      rangeWithDots.push(1, '...');
    } else {
      rangeWithDots.push(1);
    }

    rangeWithDots.push(...range);

    // Add last page and ellipsis if needed
    if (end < totalPages - 1) {
      rangeWithDots.push('...', totalPages);
    } else {
      rangeWithDots.push(totalPages);
    }

    return rangeWithDots;
  };

  const visiblePages = getVisiblePages();
  const startItem = (page - 1) * limit + 1;
  const endItem = Math.min(page * limit, total);

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: 2,
        ...sx,
      }}
    >
      {/* Page info */}
      <StyledTypography variant="body2" color="text.secondary">
        Showing {startItem}-{endItem} of {total} {itemLabel}
      </StyledTypography>

      {/* Pagination controls */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          flexWrap: 'nowrap',
        }}
      >
        {/* Previous button */}
        <IconButton
          onClick={() => onPageChange(page - 1)}
          disabled={page === 1 || loading}
          size="small"
          sx={{
            width: '32px',
            height: '32px',
          }}
        >
          <ChevronLeft />
        </IconButton>

        {/* Page numbers */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            flexWrap: 'nowrap',
          }}
        >
          {visiblePages.map((pageNum, index) => (
            <React.Fragment key={index}>
              {pageNum === '...' ? (
                <StyledTypography
                  variant="body2"
                  sx={{
                    px: 1,
                    color: 'text.secondary',
                    userSelect: 'none',
                  }}
                >
                  ...
                </StyledTypography>
              ) : (
                <StyledButton
                  noTopSpacing
                  variant={pageNum === page ? 'contained' : 'outlined'}
                  size="small"
                  onClick={() => onPageChange(pageNum as number)}
                  disabled={loading}
                  sx={{
                    minWidth: '32px',
                    height: '32px',
                    px: 1,
                    fontSize: '0.875rem',
                  }}
                >
                  {pageNum}
                </StyledButton>
              )}
            </React.Fragment>
          ))}
        </Box>

        {/* Next button */}
        <IconButton
          onClick={() => onPageChange(page + 1)}
          disabled={page === totalPages || loading}
          size="small"
          sx={{
            width: '32px',
            height: '32px',
          }}
        >
          <ChevronRight />
        </IconButton>
      </Box>
    </Box>
  );
};

export default Pagination;
